import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from './BaseComponent';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { LayoutType } from '../constants/LayoutConstants';
import { HamburgerTab, SidebarModule, PopupMenu, Overlay } from '../constants/Enums';
import { SidebarPosition, PersistentStore } from '../stores/PersistentStore';
import { ArticleStore } from '../stores/ArticleStore';
import { SceneStore } from '../stores/SceneStore';
import { UrlArgumentsStore } from '../stores/UrlArgumentsStore';
import { VisibilityStore } from '../stores/VisibilityStore';
import { PconLoginStore } from '../stores/PconLoginStore';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { UIHelper } from '../utils/UIHelper';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { SceneActions } from '../actions/SceneActions';
import { CookieBanner } from './CookieBanner';
import { CompleteLock } from './CompleteLock';
import { ConnectionError } from './ConnectionError';
import { OverlaySelectBox } from './OverlaySelectBox';
import { HamburgerButton } from './HamburgerButton';
import { InputDialog } from './InputDialog';
import { LengthInputDialog } from './LengthInputDialog';
import { MessageBox } from './MessageBox';
import { MinMaxButton } from './MinMaxButton';
import { FloatingToolbar } from './FloatingToolbar';
import { OverlayFrame } from './overlay/OverlayFrame';
import { ViewerContainer } from './ViewerContainer';
import { Sidebar } from './Sidebar';
import { SidebarTab } from './sidebar/SidebarTab';
import { HamburgerMenu } from './hamburgermenu/HamburgerMenu';
import { TopBar } from './TopBar';
import { SmartExtraPopup } from './popupmenu/SmartExtraPopup';
import { FloorPlanSetup } from './overlay/floorplan/FloorPlanSetup';
import { SolidFloorSetup } from './popupmenu/SolidFloorSetup';
import { OAPPopup } from './popupmenu/OAP/OAPPopup';
import { OAPActionsPopup } from './popupmenu/OAP/OAPActionsPopup';
import { CustomReadOnlyMessage } from './CustomReadOnlyMessage';

interface PageContainerState {
    layoutType: LayoutType;
    hamburgerTab: HamburgerTab | null;
    overlay: Overlay;
    noBars: boolean;
    connectionError: boolean;
    sidebarOrientation: SidebarPosition;
    elementsInView: boolean;
    sidebar: SidebarModule;
    allowKeyboardInput: boolean;
    userLanguage: string;
    showPrices: boolean;
    popup: PopupMenu;
    fullscreenViewer: boolean;
}

export class PageContainer extends BaseComponent<{}, PageContainerState> {
    public componentWillMount(): void {
        this.addStoreListener(ArticleStore, this.onChange);
        this.addStoreListener(PersistentStore, this.onChange);
        this.addStoreListener(SceneStore, this.onChange);
        this.addStoreListener(UrlArgumentsStore, this.onChange);
        this.addStoreListener(ViewSettingsStore, this.onChange);
        this.addStoreListener(VisibilityStore, this.onChange);
        this.addStoreListener(PconLoginStore, this.onChange);
        this.addStoreListener(ProjectManagerStore, this.onChange);
        this.onChange();
    }

    public componentDidMount(): void {
        window.addEventListener('keyup', this.keyUp);
    }

    public componentWillUnmount(): void {
        window.removeEventListener('keyup', this.keyUp);
    }

    public render(): JSX.Element {
        if (AppConfigurationStore.getState().noWebGL) {
            return (
                <div className="error-page">
                    <div className="error-icon"/>
                    <div className="error-header">{t('WebGL required')}</div>
                    <div className="error-text">{t('This application requires WebGL. Please use a WebGL-compatible Browser.')}</div>
                    <div className="error-text"><a href="http://get.webgl.org/" target="_blank">{t('Click here to get further information.')}</a></div>
                </div>
            );
        }

        return (
            <div className={this.cssClass()}>
                <ViewerContainer layoutType={this.state.layoutType} noBars={this.state.noBars} popup={this.state.popup}/>
                <div className="page-content">
                    <CookieBanner />
                    <OverlayFrame layoutType={this.state.layoutType}/>
                    <Sidebar layoutType={this.state.layoutType} sidebarOrientation={this.state.sidebarOrientation}/>
                    <SidebarTab
                        layoutType={this.state.layoutType}
                        sidebarOrientation={this.state.sidebarOrientation}
                        noBars={this.state.noBars}
                        showPrices={this.state.showPrices}
                    />
                    <CompleteLock />
                    <ConnectionError sidebarOpen={this.state.sidebar !== SidebarModule.NONE}/>
                    <HamburgerMenu
                        layoutType={this.state.layoutType}
                        sidebarOrientation={this.state.sidebarOrientation}
                        sidebar={this.state.sidebar}
                        hamburgerTab={this.state.hamburgerTab}
                        userLanguage={this.state.userLanguage}
                    />
                    <TopBar
                        layoutType={this.state.layoutType}
                        sidebarOrientation={this.state.sidebarOrientation}
                        elementsInView={this.state.elementsInView}
                        sidebar={this.state.sidebar}
                        userLanguage={this.state.userLanguage}
                    />
                    <OverlaySelectBox sidebarOrientation={this.state.sidebarOrientation} sidebar={this.state.sidebar}/>
                    <HamburgerButton layoutType={this.state.layoutType} sidebarOrientation={this.state.sidebarOrientation}/>
                    {this.state.noBars ? null : <SmartExtraPopup sidebarOrientation={this.state.sidebarOrientation}/>}
                    <InputDialog />
                    <LengthInputDialog />
                    <MessageBox />
                    <MinMaxButton sidebarOrientation={this.state.sidebarOrientation} layoutType={this.state.layoutType}/>
                    <FloatingToolbar key="toolbar-floating" elementsInView={this.state.elementsInView} layoutType={this.state.layoutType}/>
                    {this.state.popup === PopupMenu.FLOORPLAN_SETUP ? <FloorPlanSetup layoutType={this.state.layoutType}/> : null}
                    {this.state.popup === PopupMenu.SOLIDFLOOR_SETUP ? <SolidFloorSetup layoutType={this.state.layoutType}/> : null}
                    <div style={{position: 'absolute', width: '0', height: '0', overflow: 'hidden'}} id="invisible-container">
                        <img src="" id="fp-image"/>
                    </div>
                    <OAPPopup />
                    <OAPActionsPopup />
                    <CustomReadOnlyMessage />
                </div>
            </div>
        );
    }

    protected onChange = () => {
        this.setState({
            layoutType: ViewSettingsStore.getState().layoutType,
            hamburgerTab: ViewSettingsStore.getState().hamburgerTab,
            noBars: ViewSettingsStore.getState().hideBars,
            connectionError: ViewSettingsStore.getState().networkError,
            sidebarOrientation: PersistentStore.getState().sidebarPosition,
            elementsInView: SceneStore.getState().elementCount > 0,
            sidebar: ViewSettingsStore.getState().sidebar,
            allowKeyboardInput: (
                (ViewSettingsStore.getState().sidebar == null || ViewSettingsStore.getState().sidebar === SidebarModule.NONE) &&
                (VisibilityStore.getState().overlay == null || VisibilityStore.getState().overlay === Overlay.NONE) &&
                VisibilityStore.getState().dialog == null &&
                ViewSettingsStore.getState().hamburgerTab == null
            ),
            userLanguage: PersistentStore.getState().language,
            showPrices: UIHelper.showPrices(),
            popup: VisibilityStore.getState().popupMenu,
            fullscreenViewer: VisibilityStore.getState().fullscreen,
            overlay: VisibilityStore.getState().overlay
        });
    };

    protected keyUp = (event: KeyboardEvent): void => {
        if (this.state.allowKeyboardInput) {
            switch (event.keyCode) {
                case 46: // Delete
                    SceneActions.deleteSelectedElements();
                    break;
                case 45: // Insert
                    if (event.shiftKey) {
                        SceneActions.cloneSavedElements();
                    }
                    break;
                case 67: // c
                    if (event.ctrlKey) {
                        SceneActions.saveSelection();
                    }
                    break;
                case 86: // v
                    if (event.ctrlKey) {
                        SceneActions.cloneSavedElements();
                    }
                    break;
                default:
                    break;
            }
        }
    };

    private cssClass = (): string => {
        let result: string = 'page-container';
        if (this.state.noBars) {
            result += ' no-bars';
        }
        if (this.state.connectionError) {
            result += ' show-error';
        }
        if (
            AppConfigurationStore.getState().touchDevice
        ) {
            result += ' hide-ie-scroll';
        }
        if (!AppConfigurationStore.getState().touchDevice) {
            result += ' core-hover-enabled wcf-hover-enabled';
        }

        result += ' core-browser-layout';

        if (this.state.sidebarOrientation === 'right') {
            result += ' sidebar-on-right';
        } else {
            result += ' sidebar-on-left';
        }
        if (this.state.fullscreenViewer) {
            result += ' viewer-fullscreen';
        }
        if (this.state.overlay !== Overlay.NONE) {
            result += ' page-container-white-bottom';
        }

        return result;
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/PageContainer.tsx